import {useEffect, useId, useRef, useState} from 'react';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {isSameDate} from '@shared/dateUtils';

// 그래프 기본 설정
const GRAPH_HEIGHT = 160;
const GUIDE_LINE_COUNT = 4;
const TICK_COUNT = 4;
const SVG_PADDING = {left: 10, right: 10, top: 50, bottom: 30};
const GRAPH_PADDING = {left: 20, right: 20, top: 0, bottom: 0};
const GRAPH_COLOR = {secondaryHigh: 'var(--graph-gauge-active)'};

export interface DailyLineGraphProps {
  data: Array<{
    score: number;
    scoreDate: Date | null;
  }>;
  labels: Array<Date | null>;
  options: {
    initialWidth?: number;
    height?: number;
    svgPadding?: {left: number; right: number; top: number; bottom: number};
    graphPadding?: {left: number; right: number; top: number; bottom: number};
    guideLineCount?: number;
    tickCount?: number;
    barLine?: {
      color: string;
      strokeWidth: string;
      strokeDashArray: string;
    };
    max?: number;
    tooltip: {
      isShow: boolean;
      tooltipText: string;
    };
  };
  interactions: {
    selectedDataIndex: number;
    onUpdateSelectedDataIndex(selectedDataIndex: number): void;
  };
}

function DailyLineGraph({
  data,
  labels,
  options: {
    initialWidth = window.innerWidth - 90,
    height = GRAPH_HEIGHT,
    svgPadding = SVG_PADDING,
    graphPadding = GRAPH_PADDING,
    tickCount = TICK_COUNT,
    guideLineCount = GUIDE_LINE_COUNT,
    barLine = {
      color: GRAPH_COLOR.secondaryHigh,
      strokeWidth: '0.5',
      strokeDashArray: '0,0',
    },
    max,
    tooltip,
  },
  interactions: {selectedDataIndex, onUpdateSelectedDataIndex},
}: DailyLineGraphProps) {
  const chartId = useId();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipWidth, setTooltipWidth] = useState<number>(0);
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const graphLength = labels.length;

  const [width, setWidth] = useState(initialWidth);
  const svgRef = useRef<SVGSVGElement>(null);
  const maxScore = max || Math.max(...data.filter(({score}) => score !== null).map((item) => item.score), 1);

  const positionOfX = Array.from({length: graphLength}).map((value, index) => {
    if (graphLength === 1) {
      return width - svgPadding.left - svgPadding.right - graphPadding.left - graphPadding.right + svgPadding.left + graphPadding.left;
    }
    return (
      (index / (graphLength - 1)) * (width - svgPadding.left - svgPadding.right - graphPadding.left - graphPadding.right) +
      svgPadding.left +
      graphPadding.left
    );
  });

  const positionOfDataLine = data.map(({score, scoreDate}) => {
    const findIndex = labels.findIndex((labelItem) => {
      if (labelItem === null || scoreDate === null) {
        throw Error('Invalid date');
      }
      return isSameDate(labelItem, scoreDate);
    });

    if (findIndex === -1) {
      return '';
    }

    const x = positionOfX[findIndex];
    const y = height - (score / maxScore) * (height - svgPadding.top - svgPadding.bottom) - svgPadding.bottom;
    return `${x},${y}`;
  });

  const [chartIndex, setChartIndex] = useState<number>(getIndexFromData(+positionOfDataLine[selectedDataIndex].split(',')[0]));

  function getCoordinatesFromChartIndex(chartIndex: number) {
    const indexOfData = positionOfDataLine.findIndex((item) => {
      const [dataX] = item.split(',');
      return positionOfX[chartIndex] === +dataX;
    });

    if (indexOfData === -1) {
      return {
        dataIndex: 0,
        x: 0,
        y: 0,
      };
    }
    return {
      dataIndex: indexOfData,
      x: positionOfDataLine[indexOfData].split(',')[0],
      y: positionOfDataLine[indexOfData].split(',')[1],
    };
  }

  function getIndexFromData(x: number) {
    return positionOfX.findIndex((item) => item === x);
  }

  function getDataX(x: number) {
    const indexOfData = positionOfDataLine.findIndex((item, index) => {
      const [dataX] = positionOfDataLine[index].split(',');
      return x === +dataX;
    });
    return data[indexOfData];
  }

  function findAndSetChartIndex(target: EventTarget & SVGSVGElement, targetX: number) {
    const rect = target.getBoundingClientRect();
    const x = targetX - rect.left;

    const closestX = getClosestX(positionOfX, x);
    const closestIndex = getIndexFromData(closestX);

    if (getDataX(closestX) !== undefined && getDataX(closestX) !== null) {
      onUpdateSelectedDataIndex(getCoordinatesFromChartIndex(closestIndex).dataIndex);
      setChartIndex(closestIndex);
    }
  }

  function getClosestX(arr: number[], target: number): number {
    if (arr.length === 0) {
      return -1;
    }

    let closest = arr[0];
    let smallestDiff = Math.abs(target - closest);

    for (const value of arr) {
      const diff = Math.abs(target - value);
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closest = value;
      }
    }

    return closest;
  }

  function getEquidistantIndexes(length: number, count: number) {
    if (count <= 1) {
      return [0];
    }
    const array = Array.from({length}, (_, i) => i);
    return Array.from({length: count}, (_, i) => Math.round(i * ((length - 1) / (count - 1)))).map((index) => array[index]); // 추출한 인덱스를 사용하여 값 반환
  }

  useEffect(() => {
    function adjustSvgWidth() {
      if (svgRef.current) {
        setWidth(window.innerWidth - 90);
      }
    }

    if (svgRef.current) {
      svgRef.current.addEventListener('touchmove', (e) => {
        findAndSetChartIndex(e.currentTarget as EventTarget & SVGSVGElement, e.changedTouches[0].clientX);
        if (e.cancelable) {
          e.preventDefault();
        }
      });
      svgRef.current.addEventListener('click', (e) => {
        findAndSetChartIndex(e.currentTarget as EventTarget & SVGSVGElement, e.clientX);
      });
    }

    window.addEventListener('resize', adjustSvgWidth);
    return () => {
      if (svgRef.current) {
        svgRef.current.removeEventListener('touchmove', (e) => {
          findAndSetChartIndex(e.currentTarget as EventTarget & SVGSVGElement, e.changedTouches[0].clientX);
        });
        svgRef.current.removeEventListener('click', (e) => {
          findAndSetChartIndex(e.currentTarget as EventTarget & SVGSVGElement, e.clientX);
        });
      }
      window.removeEventListener('resize', adjustSvgWidth);
    };
  }, []);

  // 초기 렌더링 과정에서 width를 설정해주어야해서 useEffect 사용 필요
  useEffect(() => {
    if (tooltipRef.current) {
      const currentTooltipWidth = tooltipRef.current.offsetWidth;
      setTooltipWidth(currentTooltipWidth);
    }
  }, [chartIndex]);

  return (
    <>
      <svg
        id={`chart-${chartId}`}
        ref={svgRef}
        preserveAspectRatio="xMinYMin meet"
        width={'100%'}
        height={height}
      >
        {/* X,Y축 설정*/}
        <line
          data-name="xAxios"
          x1={svgPadding.left}
          y1={height - svgPadding.bottom}
          x2={width - svgPadding.right}
          y2={height - svgPadding.bottom}
          stroke={'var(--outline-low)'}
          strokeWidth={1}
        ></line>
        <line
          data-name="yAxios"
          x1={svgPadding.left}
          y1={height - svgPadding.bottom}
          x2={svgPadding.left}
          y2={svgPadding.top}
          strokeWidth={1}
        ></line>
        {/* X축 Tick */}
        {getEquidistantIndexes(graphLength, tickCount).map((index) => (
          <text
            aria-hidden="true"
            key={index}
            data-name={`xTick_${index}`}
            x={+positionOfX[index] - 28}
            y={height - svgPadding.bottom + 25}
            fontSize={'12'}
            fill={'var(--on-surface-container-high)'}
          >
            {CldrDatetimeFormatter.format(labels[index]!, 'short')}
          </text>
        ))}
        {/* Y축 가이드라인 */}
        {Array.from({length: guideLineCount}, (_, index) => index).map((_, index) => (
          <line
            key={index}
            data-name="y-line"
            x1={svgPadding.left}
            y1={(height - svgPadding.bottom - svgPadding.top) * (index / guideLineCount) + svgPadding.top}
            x2={width - svgPadding.right}
            y2={(height - svgPadding.bottom - svgPadding.top) * (index / guideLineCount) + svgPadding.top}
            stroke={'var(--outline-low)'}
            strokeWidth={1}
            strokeDasharray={'3,3'}
          ></line>
        ))}

        <polyline
          data-name="beforeLine"
          points={positionOfDataLine.slice(0, getCoordinatesFromChartIndex(chartIndex).dataIndex + 1).join(' ')}
          stroke={GRAPH_COLOR.secondaryHigh}
          strokeWidth="4"
          strokeLinejoin="round"
          strokeLinecap="round"
          fill="none"
        ></polyline>

        <polyline
          data-name="afterLine"
          points={positionOfDataLine.slice(getCoordinatesFromChartIndex(chartIndex).dataIndex).join(' ')}
          stroke={'var(--on-surface-container-mid)'}
          strokeWidth="4"
          strokeLinecap={'round'}
          fill="none"
          strokeLinejoin="round"
        ></polyline>
        {chartIndex !== -1 && (
          <>
            <line
              data-name="barLine"
              x1={positionOfX[chartIndex]}
              y1={svgPadding.top}
              x2={positionOfX[chartIndex]}
              y2={height - svgPadding.bottom}
              stroke={barLine.color}
              strokeWidth={barLine.strokeWidth}
              strokeDasharray={barLine.strokeDashArray}
            ></line>
            <circle
              data-name="point"
              cx={getCoordinatesFromChartIndex(chartIndex).x}
              cy={getCoordinatesFromChartIndex(chartIndex).y}
              r="7"
              fill={'var(--surface)'}
              stroke={GRAPH_COLOR.secondaryHigh}
              strokeWidth="3"
            ></circle>

            {tooltip.isShow && (
              <g>
                {/* 네모 레이블 */}
                <foreignObject
                  x={
                    positionOfX[chartIndex] < tooltipWidth / 2
                      ? svgPadding.left
                      : tooltipWidth / 2 + positionOfX[chartIndex] > width
                        ? width - tooltipWidth - svgPadding.right
                        : Math.max(svgPadding.left, positionOfX[chartIndex] - tooltipWidth / 2)
                  }
                  y={10}
                  width="100%"
                  height="100%"
                  data-name="label-rect"
                >
                  <div
                    ref={tooltipRef}
                    aria-hidden="true"
                    style={{
                      textAlign: 'center',
                      height: '25px',
                      display: 'inline-flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      color: GRAPH_COLOR.secondaryHigh,
                      border: `1px solid ${GRAPH_COLOR.secondaryHigh}`,
                      borderRadius: '5px',
                      padding: '0px 6px',
                    }}
                  >
                    {tooltip.tooltipText}
                  </div>
                </foreignObject>
                <line
                  data-name="label-triangle-1"
                  x1={positionOfX[chartIndex] - 7}
                  y1={37}
                  x2={positionOfX[chartIndex] + 7}
                  y2={37}
                  stroke="var(--surface-container)"
                  strokeWidth={2}
                />
                <line
                  data-name="label-triangle-2"
                  x1={positionOfX[chartIndex] - 7}
                  y1={37}
                  x2={positionOfX[chartIndex]}
                  y2={44}
                  stroke={GRAPH_COLOR.secondaryHigh}
                  strokeLinejoin={'round'}
                  strokeLinecap={'round'}
                />
                <line
                  data-name="label-triangle-3"
                  x1={positionOfX[chartIndex]}
                  y1={44}
                  x2={positionOfX[chartIndex] + 7}
                  y2={37}
                  stroke={GRAPH_COLOR.secondaryHigh}
                  strokeLinejoin={'round'}
                  strokeLinecap={'round'}
                />
              </g>
            )}
          </>
        )}
      </svg>
    </>
  );
}

export default DailyLineGraph;
